/**
 * useListingForm Hook
 *
 * Manages listing form state and persistence to Firestore.
 *
 * The document shape mirrors what the mobile app writes in
 * add_listing_screen.dart (and what EditListingPage reads back): a few values
 * are stored both nested and flat (capacity.* + bedrooms, amenities +
 * amenityIds, bookingSettings.* + instantBook/minNights) because different
 * readers across web, mobile and admin expect different ones.
 */

import { useState, useCallback } from 'react';
import {
  collection, doc, setDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../../services/firebase';
import { uploadFile } from '../../../../services/storage';
import { useAuth } from '../../../../contexts';

export type RoomType = 'entire_place' | 'private_room' | 'shared_room';
export type ListingStatus = 'draft' | 'pending' | 'active' | 'paused';

export interface ListingPhoto {
  id: string;
  file?: File;
  url: string;
  order: number;
  isUploading?: boolean;
}

export interface ListingLocation {
  country: string;
  state: string;
  area: string;
  city: string;
  address: string;
  fullAddress: string;
  coordinates: { lat: number; lng: number } | null;
}

export interface ListingPricing {
  nightlyRate: number;
  weekendRate: number | null;
  weeklyDiscount: number | null;
  monthlyDiscount: number | null;
  cleaningFee: number | null;
  securityDeposit: number | null;
  currency: 'USD';
}

export interface ListingHouseRules {
  smokingAllowed: boolean;
  petsAllowed: boolean;
  partiesAllowed: boolean;
  additionalRules: string;
}

export interface ListingFormData {
  // Step 1: Basics
  propertyType: string;
  roomType: RoomType;
  title: string;
  description: string;
  bedrooms: number;
  beds: number;
  bathrooms: number;
  maxGuests: number;

  // Step 2: Location
  location: ListingLocation;

  // Step 3: Amenities
  amenityIds: string[];

  // Step 4: Photos
  photos: ListingPhoto[];

  // Step 5: Pricing
  pricing: ListingPricing;

  // Step 6: Booking Settings
  instantBooking: boolean;
  minNights: number;
  maxNights: number;
  checkInTime: string;
  checkOutTime: string;
  advanceNotice: number;
  availabilityWindow: number;

  // Step 7: House Rules
  houseRules: ListingHouseRules;
  cancellationPolicy: 'flexible' | 'moderate' | 'strict';

  // Meta
  status: ListingStatus;
}

const getDefaultFormData = (): ListingFormData => ({
  propertyType: '',
  roomType: 'entire_place',
  title: '',
  description: '',
  bedrooms: 1,
  beds: 1,
  bathrooms: 1,
  maxGuests: 2,

  location: {
    country: 'Lebanon',
    state: '',
    area: '',
    city: '',
    address: '',
    fullAddress: '',
    coordinates: null,
  },

  amenityIds: [],

  photos: [],

  pricing: {
    nightlyRate: 0,
    weekendRate: null,
    weeklyDiscount: null,
    monthlyDiscount: null,
    cleaningFee: null,
    securityDeposit: null,
    currency: 'USD',
  },

  instantBooking: false,
  minNights: 1,
  maxNights: 30,
  checkInTime: '15:00',
  checkOutTime: '11:00',
  advanceNotice: 1,
  availabilityWindow: 90,

  houseRules: {
    smokingAllowed: false,
    petsAllowed: false,
    partiesAllowed: false,
    additionalRules: '',
  },
  cancellationPolicy: 'moderate',

  status: 'draft',
});

/** Shape written to `listings/{id}`. Kept in sync with the mobile writer. */
const buildListingDoc = (
  data: ListingFormData,
  photos: { url: string; order: number; caption: string }[]
) => ({
  title: data.title.trim(),
  description: data.description.trim(),
  propertyType: data.propertyType,
  roomType: data.roomType,

  capacity: {
    guests: data.maxGuests,
    bedrooms: data.bedrooms,
    beds: data.beds,
    bathrooms: data.bathrooms,
  },
  maxGuests: data.maxGuests,
  bedrooms: data.bedrooms,
  beds: data.beds,
  bathrooms: data.bathrooms,

  location: {
    address: data.location.address.trim(),
    fullAddress: (data.location.fullAddress || data.location.address).trim(),
    city: data.location.city.trim(),
    state: data.location.state,
    region: data.location.state,
    country: data.location.country || 'Lebanon',
    coordinates: data.location.coordinates
      ? {
        lat: data.location.coordinates.lat,
        lng: data.location.coordinates.lng,
        latitude: data.location.coordinates.lat,
        longitude: data.location.coordinates.lng,
      }
      : null,
  },

  pricing: {
    nightlyRate: data.pricing.nightlyRate,
    weekendRate: data.pricing.weekendRate ?? null,
    weeklyDiscount: data.pricing.weeklyDiscount ?? null,
    monthlyDiscount: data.pricing.monthlyDiscount ?? null,
    cleaningFee: data.pricing.cleaningFee ?? null,
    cleaningFeeType: 'one_time',
    securityDeposit: data.pricing.securityDeposit ?? null,
    currency: data.pricing.currency,
  },

  bookingSettings: {
    instantBooking: data.instantBooking,
    minNights: data.minNights,
    maxNights: data.maxNights,
    checkInTime: data.checkInTime,
    checkOutTime: data.checkOutTime,
  },
  instantBook: data.instantBooking,
  minNights: data.minNights,
  maxNights: data.maxNights,
  checkInTime: data.checkInTime,
  checkOutTime: data.checkOutTime,
  advanceNotice: data.advanceNotice,
  availabilityWindow: data.availabilityWindow,

  houseRules: {
    smokingAllowed: data.houseRules.smokingAllowed,
    petsAllowed: data.houseRules.petsAllowed,
    partiesAllowed: data.houseRules.partiesAllowed,
    // additionalRules is a newline textarea string in this form, but a listing
    // created on mobile stores it as an array. When such a listing is loaded
    // for editing the field arrives as an array and `.split` threw
    // ("split is not a function"), breaking Next on step 1. Accept both.
    additionalRules: (Array.isArray(data.houseRules.additionalRules)
      ? (data.houseRules.additionalRules as unknown as string[])
      : String(data.houseRules.additionalRules || '').split('\n'))
      .map((s) => String(s).trim())
      .filter(Boolean),
    checkInTime: data.checkInTime,
    checkOutTime: data.checkOutTime,
  },
  cancellationPolicy: { type: data.cancellationPolicy, rules: [] as string[] },

  amenityIds: data.amenityIds,
  amenities: data.amenityIds,
  facilityIds: [] as string[],
  photos,
});

export const useListingForm = (initialData?: Partial<ListingFormData>) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<ListingFormData>({
    ...getDefaultFormData(),
    ...initialData,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateFormData = useCallback((updates: Partial<ListingFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    setIsDirty(true);
  }, []);

  const updateLocation = useCallback((updates: Partial<ListingLocation>) => {
    setFormData((prev) => ({
      ...prev,
      location: { ...prev.location, ...updates },
    }));
    setIsDirty(true);
  }, []);

  const updatePricing = useCallback((updates: Partial<ListingPricing>) => {
    setFormData((prev) => ({
      ...prev,
      pricing: { ...prev.pricing, ...updates },
    }));
    setIsDirty(true);
  }, []);

  const updateHouseRules = useCallback((updates: Partial<ListingHouseRules>) => {
    setFormData((prev) => ({
      ...prev,
      houseRules: { ...prev.houseRules, ...updates },
    }));
    setIsDirty(true);
  }, []);

  const addPhoto = useCallback((photo: ListingPhoto) => {
    setFormData((prev) => ({
      ...prev,
      photos: [...prev.photos, photo],
    }));
    setIsDirty(true);
  }, []);

  const removePhoto = useCallback((photoId: string) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos
        .filter((p) => p.id !== photoId)
        .map((p, idx) => ({ ...p, order: idx })),
    }));
    setIsDirty(true);
  }, []);

  const reorderPhotos = useCallback((startIndex: number, endIndex: number) => {
    setFormData((prev) => {
      const result = Array.from(prev.photos);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      return {
        ...prev,
        photos: result.map((p, idx) => ({ ...p, order: idx })),
      };
    });
    setIsDirty(true);
  }, []);

  const updatePhotoUrl = useCallback((photoId: string, url: string) => {
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.map((p) =>
        p.id === photoId ? { ...p, url, isUploading: false, file: undefined } : p
      ),
    }));
  }, []);

  const toggleAmenity = useCallback((amenityId: string) => {
    setFormData((prev) => ({
      ...prev,
      amenityIds: prev.amenityIds.includes(amenityId)
        ? prev.amenityIds.filter((id) => id !== amenityId)
        : [...prev.amenityIds, amenityId],
    }));
    setIsDirty(true);
  }, []);

  /**
   * Upload any photo that is still a local blob, and swap its URL for the
   * Storage download URL. Photos already carrying an https URL are left alone,
   * so re-saving never re-uploads.
   */
  const uploadPendingPhotos = useCallback(
    async (listingId: string) => {
      const uploaded = await Promise.all(
        formData.photos.map(async (photo) => {
          if (!photo.file) {
            return { id: photo.id, url: photo.url, order: photo.order };
          }
          const stamp = Date.now();
          const { url } = await uploadFile(
            `listings/${listingId}/photo_${photo.order}_${stamp}.jpg`,
            photo.file
          );
          return { id: photo.id, url, order: photo.order };
        })
      );

      // Drop the blob references so a second save doesn't re-upload.
      setFormData((prev) => ({
        ...prev,
        photos: prev.photos.map((p) => {
          const match = uploaded.find((u) => u.id === p.id);
          if (!match || match.url === p.url) return p;
          if (p.url.startsWith('blob:')) URL.revokeObjectURL(p.url);
          return { ...p, url: match.url, file: undefined, isUploading: false };
        }),
      }));

      return uploaded
        .sort((a, b) => a.order - b.order)
        .map((p, idx) => ({ url: p.url, order: idx, caption: '' }));
    },
    [formData.photos]
  );

  /**
   * Create or update the Firestore document. `status` is only written on
   * create and on publish, so saving a draft never demotes a live listing.
   */
  const persist = useCallback(
    async (listingId: string | undefined, status?: ListingStatus): Promise<string> => {
      if (!user?.uid) throw new Error('You must be signed in to save a listing.');

      const listingRef = listingId
        ? doc(db, 'listings', listingId)
        : doc(collection(db, 'listings'));

      const photos = await uploadPendingPhotos(listingRef.id);
      const listingDoc = buildListingDoc(formData, photos);

      if (listingId) {
        await updateDoc(listingRef, {
          ...listingDoc,
          ...(status ? { status } : {}),
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(listingRef, {
          ...listingDoc,
          hostId: user.uid,
          status: status ?? 'draft',
          isFeatured: false,
          // In-app hosts are real, reachable accounts; their listings are
          // visible once published. Only unclaimed migrated hosts stay hidden.
          hostClaimed: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // First listing makes the account a host, same as the mobile flow.
        // Non-fatal: the listing itself is already saved.
        try {
          await updateDoc(doc(db, 'users', user.uid), {
            role: 'host',
            updatedAt: serverTimestamp(),
          });
        } catch (roleErr) {
          console.error('Failed to promote user to host:', roleErr);
        }
      }

      if (status) setFormData((prev) => ({ ...prev, status }));
      setIsDirty(false);
      return listingRef.id;
    },
    [formData, uploadPendingPhotos, user?.uid]
  );

  const saveDraft = useCallback(async (listingId?: string): Promise<string> => {
    // Don't create an empty document just because the host clicked Next on
    // step one — wait until there is something worth keeping.
    if (!listingId && !formData.title.trim()) return '';

    setIsSaving(true);
    setError(null);

    try {
      return await persist(listingId);
    } catch (err) {
      console.error('Failed to save draft:', err);
      setError(err instanceof Error ? err.message : 'Failed to save draft');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [formData.title, persist]);

  const publish = useCallback(async (listingId?: string): Promise<string> => {
    setIsSaving(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.title) throw new Error('Title is required');
      if (!formData.description) throw new Error('Description is required');
      if (!formData.propertyType) throw new Error('Property type is required');
      if (!formData.location.state) throw new Error('Location is required');
      if (formData.photos.length < 3) throw new Error('At least 3 photos are required');
      if (formData.pricing.nightlyRate <= 0) throw new Error('Nightly rate is required');

      // Hosts submit for review; an admin flips this to 'published' from
      // /admin/listings after the property visit.
      return await persist(listingId, 'pending');
    } catch (err) {
      console.error('Failed to publish listing:', err);
      setError(err instanceof Error ? err.message : 'Failed to publish listing');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [formData, persist]);

  const reset = useCallback(() => {
    setFormData(getDefaultFormData());
    setIsDirty(false);
    setError(null);
  }, []);

  return {
    formData,
    isDirty,
    isSaving,
    error,
    updateFormData,
    updateLocation,
    updatePricing,
    updateHouseRules,
    addPhoto,
    removePhoto,
    reorderPhotos,
    updatePhotoUrl,
    toggleAmenity,
    saveDraft,
    publish,
    reset,
  };
};

export default useListingForm;
